import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PasswordOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import AuthShell from '../components/AuthShell';
import { AuthAPI } from '../api/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { ROUTES } from '../routes';

export default function ChangePasswordPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [error, setError] = React.useState('');
  const [showOld, setShowOld] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setMsg('');
    setError('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password must match.');
      return;
    }

    setLoading(true);
    try {
      await AuthAPI.changePassword(oldPassword, newPassword);
      setMsg('Password changed successfully. Please sign in again.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await auth.logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Change password"
      subtitle="Update your current password from the secure session."
      sideTitle="Keep your account fresh"
      sideBody="This page is for authenticated users who want to rotate their password without going through reset email."
      showWorkspaceSwitchers={false}
    >
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: '#dbe7f5',
            p: { xs: 2.5, sm: 3, md: 3.5 },
            backgroundColor: '#fff',
          }}
        >
          <Stack component="form" spacing={2.2} onSubmit={submit}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Change password
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Enter your current password, then choose a new one.
              </Typography>
            </Box>

            {msg ? <Alert severity="success">{msg}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField
              label="Current Password"
              type={showOld ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowOld((prev) => !prev)} edge="end">
                      {showOld ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew((prev) => !prev)} edge="end">
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button type="submit" variant="contained" disabled={loading} startIcon={<PasswordOutlined />}>
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Change password'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </AuthShell>
  );
}
